import { CoreImageView, CoreTextView } from '@core/widgets';
import { AssetsSvgMap, THandleClick } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import clsx from 'clsx';
import React, { ElementType } from 'react';
import { TConsumerItem } from '../../../types/data';
import { TransactionStatusEnum } from '../../../types/enums';
import { TextStyle } from '../../../types/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { parseMetaText } from './metaTextUtils';
import { Tag } from '../Tag/Tag';
import styles from './styles/rowTransaction.module.css';

type TRowTransaction = {
    as: ElementType;
    handleClick?: THandleClick;
    item: TConsumerItem;
};

export function RowTransaction({ item, as, handleClick }: TRowTransaction) {
    const isDisabled = item?.enabled === false || item?.clickable === false;
    const title = item?.title;
    const subtitle = [item?.subtitle, item?.additionalSubtitle].join(' ');
    const currency = item?.currency;
    const direction = item?.direction;
    const iconName = item?.iconName;
    const { amount: metaAmount, currency: metaCurrency } = parseMetaText(item?.metaText);
    const avatar = item?.avatar;
    const flagIconSmall = item?.flagIconSmall;
    const statusFailed = item?.status ? item?.status === TransactionStatusEnum.FAIL : false;

    const getStatusIcon = (item: TConsumerItem) => {
        if (!item?.status) return;
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return 'ic_check_circle_small';
            case TransactionStatusEnum.FAIL:
                return 'ic_x_circle_small';
            default:
                return 'ic_clock_filled_small';
        }
    };

    const getIconColor = (item: TConsumerItem) => {
        if (!item?.status) return 'black';
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return item.direction === 'INCOME' ? 'purple_100' : 'black';
            case TransactionStatusEnum.FAIL:
                return 'grey_40_lc';
            default:
                return 'grey_72_lc';
        }
    };

    const getTextColor = (item: TConsumerItem) => {
        if (!item?.status) return 'black';
        switch (item?.status as TransactionStatusEnum) {
            case TransactionStatusEnum.SUCCESS:
                return item.direction === 'INCOME' ? 'purple_100' : 'black';
            case TransactionStatusEnum.SUCCESSFUL_TOPUP:
                return 'purple_100';
            case TransactionStatusEnum.FAIL:
                return 'grey_40_lc';
            default:
                return 'grey_72_lc';
        }
    };
    const ellipsis = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties;

    const iconStatus = AssetsSvgMap[getStatusIcon(item) as keyof typeof AssetsSvgMap] ?? AssetsSvgMap['ic_clock_filled_small'];
    const amountPrefix = direction === 'INCOME' ? '+' : '-';
    const RowTransactionComponent = as ?? 'button';

    const isShowInfoIcon = item?.status !== TransactionStatusEnum.NONE && item?.status !== TransactionStatusEnum.SUCCESSFUL_TOPUP;

    return (
        <RowTransactionComponent
            data-test={'rowTransaction_' + item.id}
            className={clsx(styles.commonRowTransaction, isDisabled && styles.disabled)}
            onClick={handleClick}
        >
            {iconName && (
                <VisualView
                    iconName={iconName}
                    rightMargin
                    iconHierarchy={'secondary'}
                    iconVariant={'circle'}
                    flagIconSmall={flagIconSmall}
                    size={'small'}
                    isDisabled={statusFailed}
                />
            )}
            {avatar && <VisualView avatar={avatar} rightMargin flagIconSmall={flagIconSmall} size={'small'} isDisabled={statusFailed} />}
            <div className={styles.transactionContent}>
                <div className={styles.transactionRow}>
                    <CoreTextView
                        text={title}
                        styleProps={{
                            ...TextStyle.main,
                            main: `var(--color-${statusFailed ? 'grey_40_lc' : 'black'})`,
                        }}
                        style={ellipsis}
                    />
                </div>
                {subtitle && (
                    <div className={styles.transactionRow}>
                        <CoreTextView
                            text={subtitle}
                            styleProps={{
                                ...TextStyle.secondary,
                                main: `var(--color-${statusFailed ? 'grey_40_lc' : 'grey_72_lc'}`,
                            }}
                            style={ellipsis}
                        />
                    </div>
                )}
            </div>
            <div className={styles.transactionRight}>
                {item?.amount && (
                    <div className={clsx(styles.transactionRow, styles.alignRight)}>
                        <CoreTextView
                            text={`${amountPrefix} ${item.amount} ${currency?.toLowerCase()}`}
                            styleProps={{
                                ...TextStyle.main,
                                main: `var(--color-${getTextColor(item)})`,
                                textTransformation: [
                                    {
                                        text: item.amount + '',
                                        useLocaleNumberFormat: true,
                                    },
                                    {
                                        text: currency?.toLowerCase(),
                                        smallCaps: true,
                                    },
                                ],
                            }}
                        />
                    </div>
                )}
                <ConditionalRender condition={isShowInfoIcon}>
                    <div className={clsx(styles.transactionRow, styles.alignRight)}>
                        <ConditionalRender condition={!item?.metaText && !!item?.status}>
                            <CoreImageView
                                src={getHref(iconStatus)}
                                defaultIcon={getHref('ic_clock_filled_small')}
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: `var(--color-${getIconColor(item)})`,
                                }}
                            />
                        </ConditionalRender>
                        <ConditionalRender condition={!!item?.metaText && item?.direction !== 'EXCHANGE'}>
                            <Tag metaText={item?.metaText} metaVariant="tag" />
                        </ConditionalRender>
                        <ConditionalRender condition={!!item?.metaText && item?.direction === 'EXCHANGE'}>
                            <CoreTextView
                                text={item?.metaText}
                                styleProps={{
                                    ...TextStyle.main,
                                    main: `var(--color-purple_100)`,
                                    textTransformation: [
                                        {
                                            text: metaAmount,
                                            useLocaleNumberFormat: true,
                                        },
                                        {
                                            text: metaCurrency,
                                            smallCaps: true,
                                        },
                                    ],
                                }}
                            />
                        </ConditionalRender>
                    </div>
                </ConditionalRender>

                <ConditionalRender condition={!isShowInfoIcon && !!subtitle}>
                    <div style={{ width: '16px', height: '16px' }} />
                </ConditionalRender>
            </div>
        </RowTransactionComponent>
    );
}
